import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Wheat, Leaf } from "lucide-react";
import Marquee from "../components/Marquee";
import { MENU, BRAND } from "../data/menu";

const HERO_IMG = "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?auto=format&fit=crop&w=2000&q=80";
// Story: bubbling sourdough starter jar + the trailer itself
const STORY_IMG_JAR = "/sourdough-jar.png";
const STORY_IMG_TRAILER = "https://customer-assets.emergentagent.com/job_vegan-pizza-cart/artifacts/opf4v86i_WhatsApp%20Image%202026-06-09%20at%2010.39.25%20PM.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const featured = [
    MENU[0].items[1], // Margherita
    MENU[3].items[1], // Tartufata
    MENU[0].items[3], // Caprino
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Wood-fire sourdough pizza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charred via-charred/40 to-charred/10" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 w-full">
          <motion.p
            initial="hidden" animate="show" variants={fadeUp} custom={0}
            className="text-cream/85 text-[10px] md:text-xs uppercase tracking-[0.4em] mb-6"
          >
            Est. Scarborough · Vegetarian & Vegan only
          </motion.p>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1}>
            <h1 className="font-serif text-cream text-[12vw] md:text-[8.5vw] leading-[0.86] tracking-tighter">
              Wood-fired.<br />
              <span className="italic text-cream">Sourdough Pizza.</span>
            </h1>
          </motion.div>
          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="mt-10 max-w-xl grid sm:grid-cols-2 gap-4"
          >
            <p className="text-cream/85 text-base md:text-lg leading-relaxed sm:col-span-2 mb-4">
              Authentic Neapolitan pizza, slow-fermented and blistered at 900°F
              in our live wood-fire oven on Lawrence Avenue East, Scarborough.
            </p>
            <Link to="/menu" data-testid="hero-cta-menu" className="btn-ember">
              View Menu <ArrowRight size={14} />
            </Link>
            <Link to="/locations" data-testid="hero-cta-find" className="inline-flex items-center justify-center gap-2 border-2 border-cream/80 text-cream px-7 py-4 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300 hover:bg-cream hover:text-charred">
              Visit us <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>

      </section>

      <Marquee items={["slow-fermented sourdough", "wood-fired at 900°F", "vegetarian + vegan", "made by hand", "San Marzano", "Scarborough, ON"]} />

      {/* STORY */}
      <section className="py-24 md:py-36" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}
            className="md:col-span-6"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-6">Our story</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-charred">
              A starter,<br />a flame,<br />and a <span className="italic text-ember">stubborn love</span> for the craft.
            </h2>
            <div className="mt-10 space-y-5 max-w-lg text-muted2 text-lg leading-relaxed">
              <p>Old Soul began in a borrowed kitchen with a jar of sourdough culture, a stack of San Marzano tins, and a conviction that good pizza is patient pizza.</p>
              <p>Every base is slow-fermented, hand-stretched at the counter, and licked by 900°F of seasoned oak inside our wood-fire oven in Scarborough.</p>
              <p>No meat. No shortcuts. Just craft, fire, and the most honest crust you will ever tear.</p>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-charred">
              <div className="flex items-center gap-3"><Wheat size={20} className="text-ember" /><span className="text-sm tracking-wider">Slow-fermented sourdough</span></div>
              <div className="flex items-center gap-3"><Flame size={20} className="text-ember" /><span className="text-sm tracking-wider">900°F wood-fire</span></div>
              <div className="flex items-center gap-3"><Leaf size={20} className="text-ember" /><span className="text-sm tracking-wider">Veg + Vegan</span></div>
            </div>
          </motion.div>

          <div className="md:col-span-6 md:pl-12 relative h-[560px] md:h-[720px]">
            {/* Sourdough starter jar — top right */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 w-[68%] h-[58%] overflow-hidden bg-charred/5"
            >
              <img
                src={STORY_IMG_JAR}
                alt="A jar of bubbling sourdough starter — the heart of every Old Soul pizza"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(1.05) contrast(1.04)" }}
              />
              <span className="absolute top-3 left-3 bg-cream px-3 py-2 border border-edge text-[9px] uppercase tracking-[0.3em] text-muted2">
                The mother — Day 37,102
              </span>
            </motion.div>

            {/* The trailer — bottom left, overlapping */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-0 left-0 w-[64%] h-[48%] overflow-hidden border-8 border-cream bg-charred/5 shadow-[0_20px_40px_-20px_rgba(42,36,33,0.35)]"
            >
              <img
                src={STORY_IMG_TRAILER}
                alt="The Old Soul Pizzeria wood-fire trailer parked on Lawrence Avenue East"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 55%", filter: "saturate(1.05) contrast(1.04)" }}
              />
              <span className="absolute bottom-3 right-3 bg-charred text-cream px-3 py-2 text-[9px] uppercase tracking-[0.3em]">
                Home base · Scarborough
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED PIZZAS */}
      <section className="py-24 md:py-32 bg-cream border-y border-edge" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-5">House favourites</p>
              <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-charred">Slices the regulars chase.</h2>
            </div>
            <Link to="/menu" data-testid="featured-view-all" className="text-sm uppercase tracking-[0.25em] text-charred border-b border-charred pb-1 hover:text-ember hover:border-ember transition-colors w-fit">
              Full menu
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-14">
            {featured.map((p, i) => (
              <motion.div
                key={p.name}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} custom={i}
                className="group"
                data-testid={`featured-card-${i}`}
              >
                <div className="flex items-baseline justify-between mb-3 border-b border-charred/20 pb-3">
                  <h3 className="font-serif text-3xl text-charred tracking-tight">{p.name}</h3>
                  <span className="font-serif text-2xl text-ember">${p.price.toFixed(2)}</span>
                </div>
                <p className="text-muted2 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 md:py-32" data-testid="cta-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-5">Want to eat with us?</p>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-charred">
              Pre-order pickup, or<br /><span className="italic">cater</span> your next gathering.
            </h2>
          </div>
          <div className="md:justify-self-end space-y-4">
            <p className="text-muted2 max-w-md">Drop us a message and we&rsquo;ll set up your pickup order, or get in touch about catering for your office lunch, birthday, or private gathering.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/reserve" data-testid="cta-reserve" className="btn-ember">Pickup / Catering</Link>
              <Link to="/locations" data-testid="cta-find-us" className="btn-outline-dark">Visit our oven</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
